import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { Menu, Grid, Badge } from "antd";
import IntlMessage from "components/util-components/IntlMessage";
import Icon from "components/util-components/Icon";
import navigationConfig from "configs/NavigationConfig";
import { connect } from "react-redux";
import { SIDE_NAV_LIGHT, NAV_TYPE_SIDE } from "constants/ThemeConstant";
import utils from "utils";
import { onMobileNavToggle, toggleCollapsedNav } from "redux/actions/Theme";

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

/** Agrupamento visual do menu lateral (rotas/permissões inalteradas). */
const SIDE_NAV_GROUPS = [
	{
		key: "operacao",
		title: "Operação",
		itemKeys: [
			"dashboard",
			"dashboard-teste-bateria",
			"logs",
		],
	},
	{
		key: "cadastros",
		title: "Cadastros",
		itemKeys: [
			"lista-clientes",
			"lista-centros-distribuicao",
			"lista-carregadores",
			"lista-carregadores-admin",
			"lista-middleware-releases",
			"lista-tipos-bateria",
			"lista-modelos-bateria",
			"lista-modelo-bateria-serie-override",
		],
	},
	{
		key: "relatorios",
		title: "Relatórios",
		itemKeys: ["relatorios"],
	},
	{
		key: "configuracoes",
		title: "Configurações",
		itemKeys: ["lista-usuarios", "lista-grupos-usuario"],
	},
];

const navigationByKey = navigationConfig.reduce((acc, item) => {
	acc[item.key] = item;
	return acc;
}, {});

const setLocale = (isLocaleOn, localeKey) =>
	isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString();

const setDefaultOpen = (key) => {
	let keyList = [];
	let keyString = "";
	if (key) {
		const arr = key.split("-");
		for (let index = 0; index < arr.length; index++) {
			const elm = arr[index];
			index === 0
				? (keyString = elm)
				: (keyString = `${keyString}-${elm}`);
			keyList.push(keyString);
		}
	}
	return keyList;
};

const OPERACAO_NAV_KEY = "dashboard-teste-bateria";

const SideNavContent = (props) => {
	const {
		sideNavTheme,
		routeInfo,
		hideGroupTitle,
		localization,
		onMobileNavToggle,
		toggleCollapsedNav,
		notificacoesMenu,
		listaPermissoes,
	} = props;
	const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
	const closeMobileNav = () => {
		if (isMobile) {
			onMobileNavToggle(false);
		}
	};

	const onNavItemClick = (nodeKey) => {
		closeMobileNav();
		if (nodeKey === OPERACAO_NAV_KEY && !isMobile) {
			toggleCollapsedNav(true);
		}
	};

	const verificarPermissoes = useCallback(
		(permissoes) => {
			if (permissoes === undefined || permissoes === null) return true;

			if (!Array.isArray(permissoes)) permissoes = [permissoes];

			for (const p of permissoes) {
				if (p === undefined || p === null || p === "") continue;
				if (listaPermissoes[p]) return true;
			}

			return false;
		},
		[listaPermissoes],
	);

	const menuClass = `${hideGroupTitle ? "hide-group-title " : ""}side-nav-menu`.trim();

	const renderSideNavItem = (node) => (
		<Menu.Item
			key={node.key}
			icon={node.icon ? <Icon type={node.icon} /> : undefined}
			style={{
				display: verificarPermissoes(node.permissao) ? "block" : "none",
			}}
		>
			<span>
				<Badge
					style={{ marginTop: 5 }}
					count={notificacoesMenu[node.indexNotificacoes]}
					offset={[18]}
				>
					{setLocale(localization, node.title)}
				</Badge>
			</span>
			<Link onClick={() => onNavItemClick(node.key)} to={node.path} />
		</Menu.Item>
	);

	return (
		<Menu
			theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
			mode="inline"
			style={{ height: "100%", borderRight: 0 }}
			selectedKeys={routeInfo?.key ? [routeInfo.key] : []}
			defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
			className={menuClass}
		>
			{SIDE_NAV_GROUPS.map((group) => {
				const items = group.itemKeys
					.map((key) => navigationByKey[key])
					.filter(Boolean);
				const visibleItems = items.filter((item) =>
					verificarPermissoes(item.permissao),
				);

				if (visibleItems.length === 0) {
					return null;
				}

				return (
					<Menu.ItemGroup
						key={group.key}
						className="side-nav-menu-group"
						title={group.title}
					>
						{visibleItems.map((item) => renderSideNavItem(item))}
					</Menu.ItemGroup>
				);
			})}
		</Menu>
	);
};

const TopNavContent = (props) => {
	const { topNavColor, localization } = props;

	const renderTopNavNestedItems = (items) =>
		items.map((node) =>
			node.submenu?.length > 0 ? (
				<SubMenu
					key={node.key}
					icon={node.icon ? <Icon type={node.icon} /> : undefined}
					title={setLocale(localization, node.title)}
				>
					{renderTopNavNestedItems(node.submenu)}
				</SubMenu>
			) : (
				<Menu.Item
					key={node.key}
					icon={node.icon ? <Icon type={node.icon} /> : undefined}
				>
					<span>{setLocale(localization, node.title)}</span>
					<Link to={node.path} />
				</Menu.Item>
			),
		);

	return (
		<Menu mode="horizontal" style={{ backgroundColor: topNavColor }}>
			{navigationConfig.map((menu) =>
				menu.submenu.length > 0 ? (
					<SubMenu
						key={menu.key}
						popupClassName="top-nav-menu"
						title={
							<span>
								{menu.icon ? <Icon type={menu?.icon} /> : null}
								<span>
									{setLocale(localization, menu.title)}
								</span>
							</span>
						}
					>
						{menu.submenu.map((subMenuFirst) =>
							subMenuFirst.submenu.length > 0 ? (
								<SubMenu
									key={subMenuFirst.key}
									icon={
										subMenuFirst.icon ? (
											<Icon type={subMenuFirst.icon} />
										) : undefined
									}
									title={setLocale(
										localization,
										subMenuFirst.title,
									)}
								>
									{renderTopNavNestedItems(subMenuFirst.submenu)}
								</SubMenu>
							) : (
								<Menu.Item
									key={subMenuFirst.key}
									icon={
										subMenuFirst.icon ? (
											<Icon type={subMenuFirst.icon} />
										) : undefined
									}
								>
									<span>
										{setLocale(
											localization,
											subMenuFirst.title,
										)}
									</span>
									<Link to={subMenuFirst.path} />
								</Menu.Item>
							),
						)}
					</SubMenu>
				) : (
					<Menu.Item
						key={menu.key}
						icon={menu.icon ? <Icon type={menu.icon} /> : undefined}
					>
						<span>{setLocale(localization, menu?.title)}</span>
						{menu.path ? <Link to={menu.path} /> : null}
					</Menu.Item>
				),
			)}
		</Menu>
	);
};

const MenuContent = (props) => {
	return props.type === NAV_TYPE_SIDE ? (
		<SideNavContent {...props} />
	) : (
		<TopNavContent {...props} />
	);
};

const mapStateToProps = ({ theme, menu, permissoes }) => {
	const { sideNavTheme, topNavColor } = theme;
	return {
		sideNavTheme,
		topNavColor,
		notificacoesMenu: menu,
		listaPermissoes: permissoes,
	};
};

export default connect(mapStateToProps, {
	onMobileNavToggle,
	toggleCollapsedNav,
})(MenuContent);
